/**
 * Bridge between the native mobile app (which wraps the portal in a WebView)
 * and the portal API. The app obtains the phone's push token and hands it to
 * this page; once a user is signed in, the bridge POSTs the token to
 * /api/native-push/register so the PORTAL binds it to the verified account.
 *
 * OFFICIAL contract (the shipped app's hand-over): the app injects
 *   window.AlmadinaApp = { isNativeApp, platform, pushToken }
 * and dispatches CustomEvent 'almadina-app-ready' on window — possibly more
 * than once, with pushToken null on early events and set on a later one.
 * That is channel 0 below. The generic channels are kept as fallbacks:
 *   1. URL query:        ?pushToken=… (&platform=ios|android)
 *   2. Injected globals: window.AlmadinaApp.pushToken / __PUSH_TOKEN__ /
 *                        expoPushToken / nativePushToken / EXPO_PUSH_TOKEN,
 *                        re-scanned for the first minute (injection timing
 *                        varies, and an early 'almadina-app-ready' can fire
 *                        before React mounts this bridge)
 *   3. postMessage:      window AND document (React Native on Android posts
 *                        to document), object or JSON-string payloads
 *   4. Direct call:      window.registerPushToken(token, platform)
 *
 * Guardrails: arbitrary postMessages (e.g. video players) are NOT treated as
 * tokens — a message only counts when it carries an explicit push-ish type
 * or key, or the value matches the Expo token format. Registration is
 * throttled per (token, user) pair and silently retried on later logins.
 */
import api from './api';

const LS_TOKEN = 'nativePush:token';
const LS_REG = 'nativePush:lastReg';
const REG_TTL_MS = 20 * 60 * 60 * 1000; // re-register at most ~daily per (token,user)

let installed = false;
const state = { token: null, platform: 'unknown', userId: null, inFlight: false };

function looksLikeExpoToken(s) {
  return typeof s === 'string' && /^Expo(nent)?PushToken\[[^\]]+\]$/.test(s.trim());
}

function plausibleToken(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  return t.length >= 10 && t.length <= 300;
}

function guessPlatform() {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'unknown';
}

function normPlatform(p) {
  const v = String(p || '').toLowerCase();
  return v === 'ios' || v === 'android' ? v : null;
}

function acceptToken(token, platform) {
  if (!plausibleToken(token)) return;
  state.token = String(token).trim();
  state.platform = normPlatform(platform) || (state.platform !== 'unknown' ? state.platform : guessPlatform());
  try {
    localStorage.setItem(LS_TOKEN, JSON.stringify({ token: state.token, platform: state.platform }));
  } catch (_) {}
  attempt();
}

/** Extract {token, platform} from a postMessage payload — or null. */
function fromMessageData(data) {
  if (!data) return null;
  if (typeof data === 'string') {
    const s = data.trim();
    if (looksLikeExpoToken(s)) return { token: s };
    if (s.startsWith('{')) {
      try { return fromMessageData(JSON.parse(s)); } catch (_) { return null; }
    }
    return null;
  }
  if (typeof data === 'object') {
    const type = String(data.type || data.event || data.action || '').toLowerCase();
    const named = data.pushToken || data.expoPushToken || data.nativePushToken;
    if (plausibleToken(named)) return { token: named, platform: data.platform };
    const generic = data.token || data.value;
    if (type.includes('push') && plausibleToken(generic)) return { token: generic, platform: data.platform };
    if (looksLikeExpoToken(generic)) return { token: generic, platform: data.platform };
  }
  return null;
}

async function attempt() {
  if (!state.token || !state.userId || state.inFlight) return;
  try {
    const last = JSON.parse(localStorage.getItem(LS_REG) || 'null');
    if (last && last.token === state.token && last.userId === state.userId
      && Date.now() - last.at < REG_TTL_MS) return;
  } catch (_) {}
  state.inFlight = true;
  try {
    await api.post('/native-push/register', {
      token: state.token,
      platform: state.platform,
      userAgent: navigator.userAgent || '',
    });
    try {
      localStorage.setItem(LS_REG, JSON.stringify({ token: state.token, userId: state.userId, at: Date.now() }));
    } catch (_) {}
  } catch (err) {
    // Not fatal — next login / next token event retries. 401/403 simply
    // means "not signed in yet" (or agreement gate); network errors resolve
    // on a later visit.
    // eslint-disable-next-line no-console
    console.warn('[nativePush] register failed:', err?.response?.status || err?.message);
  } finally {
    state.inFlight = false;
  }
}

/**
 * True only inside the native app's WebView (react-native-webview / Expo
 * injects this global). SECURITY: the URL-query and postMessage channels are
 * attacker-reachable in a normal browser (a phishing link can set the query;
 * any embedded cross-origin iframe can postMessage), so those channels are
 * accepted ONLY in the native environment. The injected-globals and direct
 * window.registerPushToken() channels need page-context JS execution — which
 * only the app has — so they stay open unconditionally.
 */
function isNativeEnv() {
  if (typeof window === 'undefined') return false;
  if (typeof window.ReactNativeWebView !== 'undefined') return true;
  return !!(window.AlmadinaApp && window.AlmadinaApp.isNativeApp);
}

/** The app's own hand-off object (channel 0). */
function fromAppObject(app) {
  if (app && plausibleToken(app.pushToken)) acceptToken(app.pushToken, app.platform);
}

/** Install listeners once (safe to call multiple times, inert in browsers). */
export function initNativePushListeners() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 0. Official contract: window.AlmadinaApp now + 'almadina-app-ready'
  //    events later (fires again when a late push token arrives). Only
  //    page-context JS can set that global or dispatch on window — a
  //    cross-origin iframe cannot — so no extra gating is needed.
  fromAppObject(window.AlmadinaApp);
  window.addEventListener('almadina-app-ready', (e) => fromAppObject(e && e.detail));

  // 1. URL query (cold-start deep links from the app) — native env only.
  if (isNativeEnv()) {
    try {
      const q = new URLSearchParams(window.location.search);
      const t = q.get('pushToken') || q.get('push_token') || q.get('expoPushToken');
      if (t) acceptToken(t, q.get('platform') || q.get('pushPlatform'));
    } catch (_) {}
  }

  // 2. Token saved on a previous page load (survives reloads/redirects).
  if (!state.token) {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_TOKEN) || 'null');
      if (saved && saved.token) acceptToken(saved.token, saved.platform);
    } catch (_) {}
  }

  // 3. Injected globals — now, and re-scanned briefly (injection timing varies).
  const scanGlobals = () => {
    const g = window;
    const candidates = [
      [g.AlmadinaApp && g.AlmadinaApp.pushToken, g.AlmadinaApp && g.AlmadinaApp.platform],
      [g.__PUSH_TOKEN__, g.__PUSH_PLATFORM__],
      [g.expoPushToken, g.expoPushPlatform],
      [g.nativePushToken, g.nativePushPlatform],
      [g.EXPO_PUSH_TOKEN, null],
    ];
    for (const [t, p] of candidates) {
      if (plausibleToken(t)) { acceptToken(t, p); return true; }
    }
    return false;
  };
  if (!scanGlobals()) {
    let polls = 0;
    const iv = setInterval(() => {
      polls += 1;
      if (scanGlobals() || polls >= 20) clearInterval(iv);
    }, 3000);
  }

  // 4. postMessage from the app (window for iOS/web, document for RN Android).
  //    Native env only, and never from an embedded iframe: a frame's message
  //    always carries its contentWindow as `source`, while the app's native
  //    dispatch has a null/undefined source (or the page's own window).
  const onMsg = (ev) => {
    if (!isNativeEnv()) return;
    if (ev && ev.source && ev.source !== window) return; // iframe-originated
    const found = fromMessageData(ev && ev.data);
    if (found) acceptToken(found.token, found.platform);
  };
  window.addEventListener('message', onMsg);
  document.addEventListener('message', onMsg);

  // 5. Direct hook for the app's injectedJavaScript.
  window.registerPushToken = (token, platform) => acceptToken(token, platform);
}

/** Called whenever the signed-in user changes (login/logout/boot). */
export function setNativePushUser(user) {
  const id = user ? String(user._id || user.id || '') : '';
  if (state.userId !== (id || null)) {
    state.userId = id || null;
    if (state.userId) attempt();
  }
}
